import { db } from "../firebase/config";
import { collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import {
  consultationsCollection,
  usersCollection,
  withdrawalsCollection,
} from "../firebase/firestore";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const doctorLabel = (name) => {
  const n = (name || "").trim();
  if (!n) return "Doctor";
  return /^dr\.?\s/i.test(n) ? n : `Dr. ${n}`;
};

/**
 * Builds the dashboard's "Recent activity" from real data:
 * bookings, completed / cancelled consultations, new doctors and withdrawals.
 * Each part fails on its own, so one missing index never blanks the feed.
 */
export async function getRecentActivity(max = 8) {
  const events = [];

  const consultations = getDocs(
    query(collection(db, consultationsCollection), orderBy("consultationTime", "desc"), limit(40))
  )
    .then((snap) => {
      snap.docs.forEach((d) => {
        const c = d.data();
        const patient = c.extras?.patientDetails?.patientName || c.patientName || "A patient";
        const doctor = doctorLabel(c.doctorName);
        const booked = num(c.bookedAt) || num(c.createdAt);
        if (booked) {
          events.push({ id: `b-${d.id}`, at: booked, type: "info", kind: "booking", action: "New booking", detail: `${patient} with ${doctor}` });
        }
        const ended = num(c.actualEndTime);
        if (c.videoConsultDone === true || ended > 0) {
          events.push({
            id: `c-${d.id}`,
            at: ended || num(c.consultationTime),
            type: "success",
            kind: "completed",
            action: "Consultation completed",
            detail: `${doctor} with ${patient}`,
          });
        }
        if (c.cancelledByDoctor || c.cancelledByPatient) {
          events.push({
            id: `x-${d.id}`,
            at: num(c.cancelledAt) || num(c.consultationTime),
            type: "warning",
            kind: "cancelled",
            action: c.cancelledByDoctor ? "Cancelled by doctor" : "Cancelled by patient",
            detail: `${doctor} with ${patient}`,
          });
        }
      });
    })
    .catch((e) => console.error("Activity: consultations failed", e));

  const doctors = getDocs(
    query(collection(db, usersCollection), where("type", "==", "DOCTOR"), limit(200))
  )
    .then((snap) => {
      snap.docs.forEach((d) => {
        const u = d.data();
        const at = num(u.dateOfAccountCreation) || num(u.accountCreationDate);
        if (at) {
          events.push({ id: `d-${d.id}`, at, type: "success", kind: "doctor", action: "New doctor registered", detail: doctorLabel(u.name) });
        }
      });
    })
    .catch((e) => console.error("Activity: doctors failed", e));

  const withdrawals = getDocs(collection(db, withdrawalsCollection))
    .then((snap) => {
      snap.docs.forEach((d) => {
        const w = d.data();
        const at = num(w.requestedAt) || num(w.createdAt) || num(w.date);
        if (!at) return;
        const raw = String(w.status || "pending").toLowerCase();
        const done = ["paid", "approved", "completed", "processed"].includes(raw);
        const rejected = ["rejected", "failed", "cancelled"].includes(raw);
        events.push({
          id: `w-${d.id}`,
          at,
          type: rejected ? "warning" : done ? "success" : "info",
          kind: "withdrawal",
          action: rejected ? "Withdrawal rejected" : done ? "Withdrawal paid" : "Withdrawal requested",
          detail: `${doctorLabel(w.doctorName || w.userName)} · ${inr(num(w.amount))}`,
        });
      });
    })
    .catch((e) => console.error("Activity: withdrawals failed", e));

  await Promise.all([consultations, doctors, withdrawals]);

  const now = Date.now();
  return events
    .filter((e) => e.at && e.at <= now + 60_000)
    .sort((a, b) => b.at - a.at)
    .slice(0, max);
}

export function timeAgo(ms) {
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} day${d > 1 ? "s" : ""} ago`;
  return new Date(ms).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
